<template>
  <div class="pc-layout">
    <ScaleScreen
      :width="1920"
      :height="1080"
      class="scale-wrap"
    >
      <div class="bg" :style="bgStyle">
        <!-- 头部标题区域 -->
        <header class="screen-header">
          <div class="header-content">
            <div class="time-section">
              <div class="time-wrap">
                <span class="t_icon"></span>
                <div class="current-time">{{ currentTime }}</div>
              </div>
            </div>
            <!-- <div class="weather-section">
              <span>今日天气 晴 10℃</span>
            </div> -->
            <div class="screen-section">
              <div class="screen-wrap" @click="toggleFullscreen" aria-label="切换全屏" role="button">
                <span class="s_icon"></span>
                <div class="all_txt">{{ isFullscreen ? '点击退出全屏' : '点击全屏预览' }}</div>
              </div>
              <div class="last-update">最后更新: {{ lastUpdateTime }}</div>
            </div>
          </div>
        </header>

        <!-- 主要内容区域 - PC端三栏布局 -->
        <main class="screen-main">
          <!-- 左侧区域 -->
          <section class="left-section">
            <div class="module-container">
              <BookStatistics />
            </div>
            <div class="module-container">
              <ChannelSales />
            </div>
            <div class="module-container">
              <SalesCategory />
            </div>
            <div class="module-container">
              <SalesRanking />
            </div>
          </section>

          <!-- 中央区域 -->
          <section class="center-section">
            <div class="module-container large">
              <TodaySales />
            </div>
            <div class="module-container placeholder_con">
              <!-- 时间筛选日期：今日/本周/本月 -->
              <div class="period-selector" @click.stop>
                <button :class="['selector-btn', { open: isPeriodOpen }]" @click="togglePeriodMenu">
                  <span class="label">{{ currentPeriodLabel }}</span>
                  <span class="chevron"></span>
                </button>
                <div v-if="isPeriodOpen" class="selector-menu">
                  <div
                    v-for="opt in periodOptions"
                    :key="opt.value"
                    :class="['option-item', { active: opt.value === currentPeriod }]"
                    @click="selectPeriod(opt.value)"
                  >
                    {{ opt.label }}
                  </div>
                </div>
              </div>
            </div>
            <div class="module-container map-container">
              <HunanMap 
                :dx="3" 
                :dy="2" 
                @pause-auto-refresh="onPauseAutoRefresh"
                @resume-auto-refresh="onResumeAutoRefresh"
              />
            </div>
          </section>

          <!-- 右侧区域 -->
          <section class="right-section">
            <div class="module-container">
              <TextbookStatistics />
            </div>
            <div class="module-container nothidden">
              <OrderComparison />
            </div>
            <div class="module-container">
              <CityChannelComparison />
            </div>
          </section>
        </main>
      </div>
    </ScaleScreen>
    
    <!-- 加载遮罩 -->
    <div v-if="loading" class="loading-overlay">
      <div class="loading-spinner">
        <div class="spinner"></div>
        <div class="loading-text">数据加载中...</div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import ScaleScreen from "@/components/scale-screen/scale-screen.vue";
import BookStatistics from '@/components/Modules/BookStatistics.vue'
import ChannelSales from '@/components/Modules/ChannelSales.vue'
import SalesCategory from '@/components/Modules/SalesCategory.vue'
import SalesRanking from '@/components/Modules/SalesRanking.vue'
import TodaySales from '@/components/Modules/TodaySales.vue'
import HunanMap from '@/components/Modules/HunanMap.vue'
import MemberTrend from '@/components/Modules/MemberTrend.vue'
import TextbookStatistics from '@/components/Modules/TextbookStatistics.vue'
import OrderComparison from '@/components/Modules/OrderComparison.vue'
import CityChannelComparison from '@/components/Modules/CityChannelComparison.vue'
import screenBgWebp from '@/assets/images/screen_bg.webp'
import screenBgPng from '@/assets/images/screen_bg.png'

export default {
  name: 'PCLayout',
  components: {
    ScaleScreen,
    BookStatistics,
    ChannelSales,
    SalesCategory,
    SalesRanking,
    TodaySales,
    HunanMap,
    MemberTrend,
    TextbookStatistics,
    OrderComparison,
    CityChannelComparison
  },
  props: {
    currentTime: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      bgImageUrl: '',
      // 时间选择器状态
      isPeriodOpen: false,
      // 全屏状态
      isFullscreen: false,
      currentPeriod: 'today',
      // 初始时间状态，用于自动刷新后重置
      initialPeriod: 'today',
      periodOptions: [
        { label: '今日', value: 'today' },
        { label: '本周', value: 'week' },
        { label: '本月', value: 'month' }
      ],
      // 自动刷新监听器
      refreshListener: null
    }
  },
  computed: {
    ...mapState(['loading', 'lastUpdateTime']),
    currentPeriodLabel() {
      const found = this.periodOptions.find(p => p.value === this.currentPeriod)
      return found ? found.label : '本周'
    },
    bgStyle() {
      if (!this.bgImageUrl) return {}
      return {
        backgroundImage: `url(${this.bgImageUrl})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center center',
        backgroundRepeat: 'no-repeat'
      }
    }
  },
  mounted() {
    const supportsWebp = this.checkWebpSupport()
    this.bgImageUrl = supportsWebp ? screenBgWebp : screenBgPng
    // 外部点击关闭菜单
    document.addEventListener('click', this.handleOutsideClick)
    // 监听全屏状态变化（兼容 webkit）
    document.addEventListener('fullscreenchange', this.onFullscreenChange)
    document.addEventListener('webkitfullscreenchange', this.onFullscreenChange)
    
    // 监听自动刷新事件，实现时间状态下拉框自动重置
    this.setupAutoRefreshListener()
  },
  beforeDestroy() {
    document.removeEventListener('click', this.handleOutsideClick)
    document.removeEventListener('fullscreenchange', this.onFullscreenChange)
    document.removeEventListener('webkitfullscreenchange', this.onFullscreenChange)
    
    // 清理自动刷新监听器
    this.cleanupAutoRefreshListener()
  },
  methods: {
    checkWebpSupport() {
      try {
        const elem = document.createElement('canvas')
        if (elem.getContext && elem.toDataURL) {
          const data = elem.toDataURL('image/webp')
          return data.indexOf('data:image/webp') === 0
        }
        return false
      } catch (e) {
        return false
      }
    },
    togglePeriodMenu() {
      this.isPeriodOpen = !this.isPeriodOpen
    },
    selectPeriod(val) {
      this.currentPeriod = val
      this.isPeriodOpen = false
      // 联动 TodaySales 与 HunanMap
      this.$store.dispatch('fetchTodaySales', val)
      this.$store.dispatch('fetchHunanMapData', val)
    },
    handleOutsideClick(e) {
      const el = this.$el.querySelector('.period-selector')
      if (el && !el.contains(e.target)) {
        this.isPeriodOpen = false
      }
    },
    // 全屏切换
    toggleFullscreen() {
      try {
        if (!this.isFullscreenActive()) {
          this.enterFullscreen()
        } else {
          this.exitFullscreen()
        }
      } catch (err) {
        console.error('切换全屏失败:', err)
      }
    },
    isFullscreenActive() {
      const doc = document
      return !!(
        doc.fullscreenElement ||
        doc.webkitFullscreenElement ||
        doc.msFullscreenElement
      )
    },
    enterFullscreen() {
      const el = this.$el // 全屏当前布局根节点
      const request = el.requestFullscreen || el.webkitRequestFullscreen || el.msRequestFullscreen
      if (request) {
        request.call(el)
      } else {
        console.warn('当前浏览器不支持全屏 API')
      }
    },
    exitFullscreen() {
      const exit = document.exitFullscreen || document.webkitExitFullscreen || document.msExitFullscreen
      if (exit) {
        exit.call(document)
      } else {
        console.warn('当前浏览器不支持退出全屏 API')
      }
    },
    onFullscreenChange() {
      this.isFullscreen = this.isFullscreenActive()
    },
    
    // 设置自动刷新监听器
    setupAutoRefreshListener() {
      // 监听 Vuex store 中的数据重置事件
      this.refreshListener = this.$store.watch(
        (state) => state.loading,
        (newLoading, oldLoading) => {
          // 当加载状态从 true 变为 false 时，表示自动刷新完成
          if (oldLoading && !newLoading) {
            this.resetTimeSelector()
          }
        }
      )
    },
    
    // 清理自动刷新监听器
    cleanupAutoRefreshListener() {
      if (this.refreshListener) {
        this.refreshListener() // 调用返回的取消监听函数
        this.refreshListener = null
      }
    },
    
    // 重置时间选择器到初始状态
    resetTimeSelector() {
      this.currentPeriod = this.initialPeriod
      this.isPeriodOpen = false
      
      // 重新获取初始时间状态的数据
      this.$store.dispatch('fetchTodaySales', this.initialPeriod)
      this.$store.dispatch('fetchHunanMapData', this.initialPeriod)
    },
    // 接收地图组件的暂停/恢复自动刷新事件并向上抛出
    onPauseAutoRefresh() {
      this.$emit('pause-auto-refresh')
    },
    onResumeAutoRefresh() {
      this.$emit('resume-auto-refresh')
    }
  }
}
</script>

<style lang="scss" scoped>
.pc-layout {
  width: 100vw;
  height: 100vh;
  background: #0F3171;
  color: #ffffff;
  font-family: 'Microsoft YaHei', Arial, sans-serif;
  overflow: hidden;
  position: relative;

  .bg {
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
  }

  // 头部样式
  .screen-header {
    width: 100%;
    height: 70px;
    display: flex;
    align-items: center;
    padding: 0 25px;
    flex-shrink: 0;
    position: relative;
    z-index: 10;

    .header-content {
      width: 100%;
      display: flex;
      align-items: center;

      .time-section {
        position: relative;
        .time-wrap {
          display: flex;
          align-items: center;
          margin-bottom: 5px;
          .t_icon {
            width: 16px;
            height: 16px;
            background: url("@/assets/images/time_icon.png") no-repeat center center;
            background-size: 100% auto;
            margin-right: 10px;
          }

          .current-time {
            font-size: 16px;
            font-weight: 400;
            color: #FFFFFF;
          }
        }
      }

      .weather-section {
        display: flex;
        align-items: center;
        font-size: 14px;
        color: #fff;
        font-weight: 400;
        margin-right: 34px;
        margin-left: auto;
      }

      .screen-section {
        margin-left: auto;
        // margin-left: 34px;
        position: relative;
        .screen-wrap {
          display: flex;
          align-items: center;
          margin-bottom: 5px;
          cursor: pointer;
          .s_icon {
            width: 16px;
            height: 16px;
            background: url("@/assets/images/all_screen.png") no-repeat center center;
            background-size: 100% auto;
            margin-right: 14px;
          }

          .all_txt {
            font-size: 14px;
            font-weight: 400;
            color: #FFFFFF;
          }
        }
        .last-update {
          position: absolute;
          top: 100%;
          right: 0;
          font-size: 12px;
          color: #8c8c8c;
          white-space: nowrap;
        }
      }
    }
  }

  // 主要内容区域 - PC端三栏布局
  .screen-main {
    flex: 1;
    min-height: 0;
    display: flex;
    padding: 0 10px 10px;

    .left-section,
    .right-section {
      flex: 0 0 450px;
      display: flex;
      flex-direction: column;
      box-sizing: border-box;
      padding: 20px;
      background-color: rgba(0, 0, 0, 0.16);
      border-radius: 10px;
      position: relative;
      z-index: 10;
    }

    .center-section {
      flex: 1;
      display: flex;
      flex-direction: column;
      box-sizing: border-box;
      padding: 20px 108px;
      gap: 20px;
    }

    .module-container {
      width: 100%;
      height: auto;
      box-sizing: border-box;
      // flex: 1;
      flex-grow: 1;
      min-height: 100px;
      overflow: hidden;
      position: relative;

      &.large {
        flex-shrink: 0;
        min-height: 160px;
        position: relative;
        z-index: 10;
      }

      &.placeholder_con {
        flex: 1;
        width: 100%;
        min-height: 780px;
        overflow: visible;

        // 时间下拉选择框容器定位（靠近顶部左侧）
        .period-selector {
          position: absolute;
          right: 0;
          top: -35px;
          padding: 0;
          z-index: 50;
        }

        .selector-btn {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          height: 27px;
          padding: 0 12px;
          border-radius: 16px;
          border: 1px solid #28437A;
          background-color: transparent;
          color: #56A9ED;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .selector-btn:hover,
        .selector-btn.open {
          border-color: rgba(166, 234, 254, 0.6);
          color: #FFF;
        }

        .label {
          font-size: 12px;
          font-weight: 400;
          letter-spacing: 0.5px;
        }

        .chevron {
          width: 0;
          height: 0;
          border-left: 6px solid transparent;
          border-right: 6px solid transparent;
          border-top: 6px solid #A6B5C9;
          opacity: 0.9;
          transition: transform 0.2s ease;
        }

        .selector-btn.open .chevron {
          transform: rotate(180deg);
        }

        // 下拉菜单
        .selector-menu {
          position: absolute;
          left: 50%;
          top: 32px;
          transform: translateX(-50%);
          min-width: 63px;
          background: transparent;
          backdrop-filter: blur(2px);
          border: 1px solid rgba(166, 234, 254, 0.35);
          border-radius: 8px;
          box-shadow: 0 4px 10px rgba(0, 0, 0, 0.25);
          overflow: hidden;
          z-index: 10;
        }

        .option-item {
          font-size: 12px;
          color: #d9f6ff;
          padding: 6px 12px;
          white-space: nowrap;
          cursor: pointer;
          transition: background 0.2s ease, color 0.2s ease;
        }

        .option-item:hover {
          background: rgba(166, 234, 254, 0.15);
          color: #ffffff;
        }

        .option-item.active {
          background: rgba(166, 234, 254, 0.25);
          color: #ffffff;
          font-weight: 600;
        }
      }

      &.map-container {
        width: 100%;
        height: 100%;
        position: absolute;
        top: 0;
        left: 0;
        box-sizing: border-box;
        padding: 230px 460px 44px;
      }
    }
  }
  
  .nothidden {
    overflow: visible !important;
  }

  // 加载遮罩样式
  .loading-overlay {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.6);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 9999;

    .loading-spinner {
      text-align: center;

      .spinner {
        width: 50px;
        height: 50px;
        border: 4px solid rgba(24, 144, 255, 0.4);
        border-top: 4px solid #1890ff;
        border-radius: 50%;
        animation: spin 1s linear infinite;
        margin: 0 auto 20px;
      }

      .loading-text {
        color: #1890ff;
        font-size: 16px;
        font-weight: bold;
      }
    }
  }

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
}
</style>